import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/api";
import { PdfDropZone, PdfFileList, PdfUrlForm, mergeFiles } from "../pdf/PdfUpload";
import { fetchPdfBlobUrl, fileNameFromUrl, formatFileSize, isLocalPdfKey } from "../../utils/pdfFiles";
import { showToast } from "../layout/Toast";
import "./HomePage.css";

const DEFAULT_WS_NAMES = new Set(["e-diary", "initial workspace", "main"]);

const timeAgo = (value) => {
    if (!value) return "";
    const date = typeof value === "number" ? new Date(value * 1000) : new Date(value);
    const seconds = Math.round((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return "just now";
    const units = [["year", 31536000], ["month", 2592000], ["day", 86400], ["hour", 3600], ["minute", 60]];
    for (const [unit, size] of units) {
        const n = Math.floor(seconds / size);
        if (n >= 1) return `${n} ${unit}${n > 1 ? "s" : ""} ago`;
    }
    return "just now";
};

const workspaceTitle = (ws) => {
    if (ws.name && !DEFAULT_WS_NAMES.has(ws.name.trim().toLowerCase())) return ws.name;
    const first = ws.pdfs[0]?.label || ws.pdfs[0]?.name || "Untitled workspace";
    return first.replace(/\.pdf$/i, "");
};

/** Small modal used for upload and delete confirmation. */
function Dialog({ title, onClose, children, width = 480 }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div className="pdfu-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="pdfu-modal" role="dialog" aria-label={title} style={{ width }}>
                <div className="pdfu-modal-head">
                    <span className="pdfu-modal-title">{title}</span>
                    <button className="pdfu-icon-btn" onClick={onClose} title="Close"><i className="bi bi-x-lg" /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

function UploadPanel({ onOpenFiles, onOpenUrl, compact }) {
    const [files, setFiles] = useState([]);
    return (
        <>
            <PdfDropZone compact={compact} onFiles={incoming => setFiles(prev => mergeFiles(prev, incoming))} />
            <PdfFileList files={files} onRemove={i => setFiles(prev => prev.filter((_, idx) => idx !== i))} />
            {files.length > 0 && (
                <div className="pdfu-actions">
                    <button className="pdfu-btn pdfu-btn--primary" onClick={() => onOpenFiles(files)}>
                        <i className="bi bi-cloud-arrow-up" /> Upload & open {files.length} PDF{files.length > 1 ? "s" : ""}
                    </button>
                </div>
            )}
            <div className="pdfu-divider">or open from a link</div>
            <PdfUrlForm onLoad={onOpenUrl} />
        </>
    );
}

function RowMenu({ items }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
        document.addEventListener("pointerdown", close);
        return () => document.removeEventListener("pointerdown", close);
    }, [open]);
    return (
        <div className="home-menu" ref={ref} onClick={e => e.stopPropagation()}>
            <button className="home-icon-btn" title="More actions" onClick={() => setOpen(v => !v)}>
                <i className="bi bi-three-dots-vertical" />
            </button>
            {open && (
                <div className="home-menu-list">
                    {items.map(item => (
                        <button
                            key={item.label}
                            className={`home-menu-item ${item.danger ? "is-danger" : ""}`}
                            onClick={() => { setOpen(false); item.onClick(); }}
                        >
                            <i className={`bi ${item.icon}`} /> {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function WorkspaceCard({ ws, onOpen, onRename, onDelete, busy }) {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState("");
    const title = workspaceTitle(ws);
    const files = ws.pdfs;

    const submitRename = (e) => {
        e.preventDefault();
        const next = name.trim();
        setEditing(false);
        if (next && next !== title) onRename(ws, next);
    };

    return (
        <div
            className={`home-ws-card ${busy ? "is-busy" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => !editing && onOpen(ws)}
            onKeyDown={e => { if (e.key === "Enter" && !editing) onOpen(ws); }}
        >
            <div className="home-ws-top">
                <div className="home-ws-icon"><i className="bi bi-collection" /></div>
                {editing ? (
                    <form className="home-rename" onSubmit={submitRename} onClick={e => e.stopPropagation()}>
                        <input
                            autoFocus
                            value={name}
                            maxLength={120}
                            onChange={e => setName(e.target.value)}
                            onBlur={submitRename}
                            onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); setEditing(false); } }}
                        />
                    </form>
                ) : (
                    <div className="home-ws-title" title={title}>{title}</div>
                )}
                {busy
                    ? <span className="pdfu-spinner home-ws-spinner" />
                    : (
                        <RowMenu items={[
                            { label: "Open", icon: "bi-box-arrow-up-right", onClick: () => onOpen(ws) },
                            { label: "Rename", icon: "bi-pencil", onClick: () => { setName(title); setEditing(true); } },
                            { label: "Delete", icon: "bi-trash3", danger: true, onClick: () => onDelete(ws) },
                        ]} />
                    )}
            </div>
            <ul className="home-ws-files">
                {files.slice(0, 3).map(p => (
                    <li key={p.pdf_id} title={p.label || p.name}>
                        <i className="bi bi-file-earmark-pdf" />
                        <span>{p.label || p.name}</span>
                    </li>
                ))}
                {files.length > 3 && <li className="home-ws-more">+{files.length - 3} more</li>}
            </ul>
            <div className="home-ws-meta">
                <span>{files.length} PDF{files.length > 1 ? "s" : ""}</span>
                {ws.workspace_names.length > 1 && <span>· {ws.workspace_names.length} boards</span>}
                {ws.updated_at && <span>· Updated {timeAgo(ws.updated_at)}</span>}
            </div>
        </div>
    );
}

/**
 * Per-user home page (shown when no PDF is open): the user's workspaces and uploaded files,
 * like a small file manager — open, rename, delete, upload new.
 */
export default function HomePage({ userId, onSelect, onSelectFiles, onOpenWorkspace, onSwitchUser }) {
    const [view, setView] = useState("workspaces");
    const [query, setQuery] = useState("");
    const [workspaces, setWorkspaces] = useState(null); // null = loading
    const [files, setFiles] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [showUpload, setShowUpload] = useState(false);
    const [confirm, setConfirm] = useState(null);       // { title, message, actionLabel, run }
    const [busyKey, setBusyKey] = useState(null);       // workspace id / file path being opened
    const [opening, setOpening] = useState("");         // full-screen "Opening…" label

    const load = useCallback(async () => {
        setLoadError("");
        const [wsRes, fileRes] = await Promise.allSettled([api.listMyWorkspaces(), api.listUploadedPdfs()]);
        if (wsRes.status === "fulfilled") setWorkspaces(wsRes.value.data || []);
        else { setWorkspaces([]); setLoadError("Couldn't reach the server — your workspaces aren't shown."); }
        if (fileRes.status === "fulfilled") setFiles(fileRes.value.data || []);
        else setFiles([]);
    }, []);
    useEffect(() => { load(); }, [load]);

    const q = query.trim().toLowerCase();
    const shownWorkspaces = useMemo(() => (workspaces || []).filter(ws =>
        !q || workspaceTitle(ws).toLowerCase().includes(q) || ws.pdfs.some(p => (p.label || p.name || "").toLowerCase().includes(q))
    ), [workspaces, q]);
    const shownFiles = useMemo(() => (files || []).filter(f => !q || f.name.toLowerCase().includes(q)), [files, q]);

    // ── Actions ──
    const openWorkspace = async (ws) => {
        if (busyKey) return;
        const available = ws.pdfs.filter(p => p.url && !isLocalPdfKey(p.url));
        if (!available.length) {
            showToast("This workspace's PDFs were never uploaded to the server, so they can't be reopened.", "warning", 5000);
            return;
        }
        setBusyKey(ws.id);
        setOpening(`Opening "${workspaceTitle(ws)}"…`);
        const ok = await onOpenWorkspace(ws);
        // On success this page unmounts; only reset when we're still here.
        if (!ok) {
            showToast("Couldn't open this workspace — its files may have been removed from the server.", "error", 5000);
            setBusyKey(null);
            setOpening("");
        }
    };

    const openFile = async (file) => {
        if (busyKey) return;
        setBusyKey(file.path);
        setOpening(`Opening "${file.name}"…`);
        let ok = false;
        try {
            ok = await onSelect(await fetchPdfBlobUrl(file.path), file.name, file.path);
        } catch (err) {
            showToast(`Couldn't open "${file.name}": ${err.message}`, "error", 5000);
        }
        if (!ok) {
            setBusyKey(null);
            setOpening("");
        }
    };

    const uploadFiles = async (picked) => {
        setShowUpload(false);
        setOpening(`Uploading ${picked.length} PDF${picked.length > 1 ? "s" : ""}…`);
        // The page unmounts as soon as the first PDF is open; the rest keep loading.
        const opened = await onSelectFiles(picked);
        if (!opened) setOpening("");
    };

    const openUrl = async (url) => {
        const blobUrl = await fetchPdfBlobUrl(url); // throws → shown under the link field
        setShowUpload(false);
        setOpening("Opening PDF…");
        if (!(await onSelect(blobUrl, fileNameFromUrl(url), url))) setOpening("");
    };

    const renameWorkspace = async (ws, name) => {
        const previous = workspaces;
        setWorkspaces(list => list.map(w => w.id === ws.id ? { ...w, name } : w));
        try {
            await api.renameWorkspace(ws.id, name);
            showToast("Workspace renamed", "success");
        } catch (err) {
            setWorkspaces(previous);
            showToast(err.response?.data?.detail || "Rename failed", "error");
        }
    };

    const askDeleteWorkspace = (ws) => setConfirm({
        title: "Delete workspace?",
        message: `"${workspaceTitle(ws)}" and all its notes, images and connections will be deleted. Your uploaded PDFs stay in Files.`,
        actionLabel: "Delete workspace",
        run: async () => {
            await api.deleteWorkspaceGroup(ws.id);
            setWorkspaces(list => list.filter(w => w.id !== ws.id));
            showToast("Workspace deleted", "success");
        },
    });

    const askRemoveFile = (file) => setConfirm({
        title: "Remove file?",
        message: `"${file.name}" will be removed from your files. Workspaces that use it keep working.`,
        actionLabel: "Remove",
        run: async () => {
            await api.removeUploadedPdf(file.path);
            setFiles(list => list.filter(f => f.path !== file.path));
            showToast("File removed", "success");
        },
    });

    const runConfirm = async () => {
        const { run } = confirm;
        setConfirm(c => ({ ...c, running: true }));
        try {
            await run();
            setConfirm(null);
        } catch (err) {
            showToast(err.response?.data?.detail || err.message || "Something went wrong", "error");
            setConfirm(c => ({ ...c, running: false }));
        }
    };

    const loading = workspaces === null || files === null;
    const isEmpty = !loading && workspaces.length === 0 && files.length === 0;

    return (
        <div className="home">
            <header className="home-header">
                <div className="pdfu-brand" style={{ margin: 0 }}>
                    <div className="pdfu-brand-mark"><i className="bi bi-file-earmark-richtext" /></div>
                    <span className="pdfu-brand-name">AirPDF Workspace</span>
                </div>
                <div className="home-search">
                    <i className="bi bi-search" />
                    <input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search workspaces and files"
                    />
                </div>
                <div className="home-user">
                    <div className="home-avatar">{userId.charAt(0).toUpperCase()}</div>
                    <span className="home-user-name" title={userId}>{userId}</span>
                    <button className="home-link-btn" onClick={onSwitchUser} title="Sign in as a different user">
                        Switch user
                    </button>
                </div>
            </header>

            <div className="home-body">
                <nav className="home-nav">
                    <button className="pdfu-btn pdfu-btn--primary home-new-btn" onClick={() => setShowUpload(true)}>
                        <i className="bi bi-plus-lg" /> New upload
                    </button>
                    <button className={`home-nav-item ${view === "workspaces" ? "is-active" : ""}`} onClick={() => setView("workspaces")}>
                        <i className="bi bi-collection" /> Workspaces
                        {workspaces && <span className="home-count">{workspaces.length}</span>}
                    </button>
                    <button className={`home-nav-item ${view === "files" ? "is-active" : ""}`} onClick={() => setView("files")}>
                        <i className="bi bi-folder2" /> Files
                        {files && <span className="home-count">{files.length}</span>}
                    </button>
                </nav>

                <main className="home-main">
                    {loadError && <div className="pdfu-drop-warn home-error">{loadError}</div>}

                    {loading ? (
                        <div className="pdfu-loading home-center"><span className="pdfu-spinner" /> Loading your workspace…</div>
                    ) : isEmpty ? (
                        <div className="home-empty">
                            <h1 className="pdfu-title">Welcome, {userId} 👋</h1>
                            <p className="pdfu-subtitle">Upload your first PDF to start a workspace. Everything you do is saved to your account.</p>
                            <div className="home-empty-card">
                                <UploadPanel onOpenFiles={uploadFiles} onOpenUrl={openUrl} />
                            </div>
                        </div>
                    ) : view === "workspaces" ? (
                        <>
                            <div className="home-section-head">
                                <h2>Workspaces</h2>
                                <span>Pick up where you left off</span>
                            </div>
                            {shownWorkspaces.length === 0 ? (
                                <div className="home-placeholder">
                                    {q ? `No workspaces match "${query}".` : "No workspaces yet — upload a PDF to start one."}
                                </div>
                            ) : (
                                <div className="home-ws-grid">
                                    {shownWorkspaces.map(ws => (
                                        <WorkspaceCard
                                            key={ws.id}
                                            ws={ws}
                                            busy={busyKey === ws.id}
                                            onOpen={openWorkspace}
                                            onRename={renameWorkspace}
                                            onDelete={askDeleteWorkspace}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="home-section-head">
                                <h2>Files</h2>
                                <span>PDFs you've uploaded</span>
                            </div>
                            {shownFiles.length === 0 ? (
                                <div className="home-placeholder">
                                    {q ? `No files match "${query}".` : "No uploaded files yet."}
                                </div>
                            ) : (
                                <div className="home-table" role="table">
                                    <div className="home-table-row home-table-head" role="row">
                                        <span>Name</span><span>Size</span><span>Uploaded</span><span />
                                    </div>
                                    {shownFiles.map(file => (
                                        <div
                                            key={file.path}
                                            className="home-table-row"
                                            role="row"
                                            tabIndex={0}
                                            onClick={() => openFile(file)}
                                            onKeyDown={e => { if (e.key === "Enter") openFile(file); }}
                                        >
                                            <span className="home-file-name" title={file.name}>
                                                <i className="bi bi-file-earmark-pdf-fill" />
                                                <span>{file.name}</span>
                                            </span>
                                            <span className="home-muted">{formatFileSize(file.size)}</span>
                                            <span className="home-muted">{timeAgo(file.uploaded_at)}</span>
                                            <span className="home-row-actions">
                                                {busyKey === file.path
                                                    ? <span className="pdfu-spinner home-ws-spinner" />
                                                    : (
                                                        <RowMenu items={[
                                                            { label: "Open", icon: "bi-box-arrow-up-right", onClick: () => openFile(file) },
                                                            { label: "Remove from files", icon: "bi-trash3", danger: true, onClick: () => askRemoveFile(file) },
                                                        ]} />
                                                    )}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>

            {showUpload && (
                <Dialog title="Upload PDFs" onClose={() => setShowUpload(false)}>
                    <UploadPanel compact onOpenFiles={uploadFiles} onOpenUrl={openUrl} />
                </Dialog>
            )}

            {confirm && (
                <Dialog title={confirm.title} onClose={() => !confirm.running && setConfirm(null)} width={420}>
                    <p className="home-confirm-text">{confirm.message}</p>
                    <div className="home-confirm-actions">
                        <button className="pdfu-btn pdfu-btn--secondary" onClick={() => setConfirm(null)} disabled={confirm.running}>Cancel</button>
                        <button className="pdfu-btn home-btn-danger" onClick={runConfirm} disabled={confirm.running}>
                            {confirm.running ? <span className="pdfu-spinner" /> : confirm.actionLabel}
                        </button>
                    </div>
                </Dialog>
            )}

            {opening && (
                <div className="home-opening">
                    <div className="pdfu-loading"><span className="pdfu-spinner" />{opening}</div>
                </div>
            )}
        </div>
    );
}
